import type { CUserAccountsTranslations } from "./pl";

export const en: CUserAccountsTranslations = {
  empty: "No data to display.",
  registerDisabled: "Registration is disabled.",

  logout: {
    failed: "Failed to log out.",
  },

  actions: {
    login: "Log in",
    register: "Create account",
    remindPass: "Forgot password?",
    backToLogin: "Back to login",
    logout: "Log out",
  },

  labels: {
    login: "Login or email",
    pass: "Password",
    rememberMe: "Remember me for 7 days",
    email: "Email",
    userGroup: "User group",
    name: "First name",
    surname: "Last name",
    telephone: "Phone",
    passwordRegister: "Password",
    ppass: "Repeat password",
    regulamin: "I accept the terms and conditions",
    regulaminLink: "see",
    personalData: "I consent to the processing of my personal data.",
    marketing: "Marketing communication consent",
    emailOrLogin: "Email or login",
  },

  validation: {
    loginRequired: "Enter login or email address.",
    passwordRequired: "Enter password.",
    emailRequired: "Email field is required.",
    invalidEmail: "Invalid email address.",
    passwordRegRequired: "Password is required.",
    passwordRepeatRequired: "Repeat password.",
    passwordMismatch: "Passwords do not match.",
    passwordRequirements:
      "Password does not meet the requirements — see the list below the field.",
    requiredRegulamin: "Terms acceptance is required.",
    requiredPersonalData: "Consent to personal data processing is required.",
    emailOrLoginRequired: "Enter email or login.",
    newPassRequired: "Enter new password.",
    newPpassRequired: "Repeat new password.",
  },

  passwordStrength: {
    strengthLabel: "Password strength",
    levels: {
      veryWeak: "Very weak",
      weak: "Weak",
      fair: "Fair",
      good: "Strong",
    },
    requirements: {
      length: "{min}–{max} characters",
      lowercase: "Lowercase letter (a–z)",
      uppercase: "Uppercase letter (A–Z)",
      digit: "Digit (0–9)",
      special: "Special character (!@#$…)",
    },
  },

  login: {
    failed: "Login failed.",
  },

  register: {
    failed: "Registration failed.",
    successDefault: "Account created. Check your email to activate.",
    successWithEmail:
      "Registration successful! An activation link has been sent to {email}. Please check your inbox AND your SPAM/Junk folder — the message may end up there instead of your main inbox.",
    successMailFailed:
      "Your account has been created, but we couldn't send the activation email to {email}. Please contact the site administrator or request a new verification link after logging in.",
    recaptchaHint:
      "Backend requires reCAPTCHA — the reCAPTCHA component for Imperial CMS will be added in the next iteration. For now, server-side validation is tolerant (test mode).",
  },

  remind: {
    title: "Forgot password",
    hint: "Enter your email or login. We will send you a reset link.",
    submit: "Send link",
    failed: "Failed to send reset link.",
    successDefault: "If the account exists, a password reset link has been sent to the email.",
  },

  reset: {
    title: "Set new password",
    newPass: "New password",
    repeatPass: "Repeat new password",
    submit: "Change password",
    invalidLink: "Invalid password reset link. Try sending a new one.",
    failed: "Failed to change password.",
    successDefault: "Password has been changed. You can now log in.",
  },

  verifySetPassword: {
    title: "Account activation",
    pending: "Activating your account...",
    invalidLink: "Invalid activation link. Use the password reminder instead.",
    failed:
      "The account could not be activated. The link may have expired or been used already - use the password reminder instead.",
  },

  profile: {
    loading: "Loading profile data…",
    loadFailed: "Failed to load profile data.",
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
    tabs: {
      personal: "Your data",
      password: "Change password",
      account: "Account",
      users: "Users",
      security: "Security",
    },
  },

  security: {
    title: "Two-factor authentication",
    loading: "Loading security settings…",
    loadFailed: "Failed to load security settings.",

    setupIntro: "Two-factor authentication (2FA) adds an extra layer of protection to your account. On sign-in you will provide a 6-digit code from your phone app in addition to your password.",
    actionSetupStart: "Generate key",
    setupFailed: "Failed to start the setup. Please try again.",

    step1Title: "Scan the QR code",
    step1Description: "Open your authenticator app (Google Authenticator, Microsoft Authenticator) and scan the QR code below.",
    step2Title: "Enter the code from the app",
    step2Description: "The app will generate a 6-digit code that rotates every 30 seconds. Enter it below.",

    qrAlt: "QR code for authenticator app",
    qrManualEntry: "Cannot scan? Enter this key manually:",

    actionEnable: "Enable authentication",
    enableFailed: "Failed to enable authentication. Please try again.",
    invalidCode: "Invalid code. Check the code in your app and try again.",
    rateLimitExceeded: "Too many failed attempts. Please start the setup again to generate a new key.",

    enabledTitle: "Two-factor authentication is active",
    enabledDescription: "Your account is protected by an additional security layer. A code from your authenticator app is required at sign-in.",
    disablePasswordLabel: "Current password",
    actionDisable: "Disable authentication",
    disableFailed: "Failed to disable authentication.",
    invalidPassword: "Invalid password. Check the spelling and try again.",
    disableSuccess: "Two-factor authentication has been disabled.",
    requiredByGroupConfigured: "Your user group requires active two-factor authentication - disabling is not permitted.",
  },

  mfaGuard: {
    title: "Two-factor authentication required",
    description: "Your user group requires active two-factor authentication. Configure MFA in the Security tab to improve your account protection.",
    linkToSecurity: "Go to setup",
  },

  authMfaModal: {
    title: "Two-factor authentication",
    intro: "Enter the 6-digit code from your authenticator app (Google Authenticator, Microsoft Authenticator).",
    close: "Close",
    verify: "Verify",
    cancel: "Cancel",
    invalidCode: "Invalid code. Please try again.",
    sessionExpired: "Verification session has expired.",
    ariaLabel: "Two-factor authentication",
  },

  userList: {
    title: "Users",
    loading: "Loading user list…",
    loadFailed: "Failed to load the user list.",
    empty: "No users to display.",
    searchPlaceholder: "Search...",
    columnUser: "User",
    columnEmail: "E-mail",
    columnPhone: "Phone",
    columnAction: "Action",
    sendMessage: "Send message",
    messagesUnavailable: "unavailable",
    noEmail: "none",
    noPhone: "none",
    truncatedHint: "The list has been limited to the first entries.",
    paginationPrev: "Previous",
    paginationNext: "Next",
    paginationInfo: "Page {page} of {pages}",
  },
};
